import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { Button } from '../components/ui/Button';

interface ResetPasswordProps {
    onSuccess: () => void;
}

export const ResetPassword: React.FC<ResetPasswordProps> = ({ onSuccess }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

    useEffect(() => {
        // Check if we actually have a session to reset the password for
        // Supabase usually logs the user in when they click the reset link
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session) {
                setMessage({ type: 'error', text: 'El enlace de recuperación es inválido o ha expirado.' });
            }
        });

        // Handle token from URL if it's there
        supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                setMessage({ type: 'success', text: 'Por favor, ingresa tu nueva contraseña.' });
            }
        });
    }, []);

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setMessage({ type: 'error', text: 'Las contraseñas no coinciden.' });
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;

            setMessage({ type: 'success', text: '¡Contraseña actualizada con éxito!' });
            setTimeout(() => {
                onSuccess();
            }, 2000);
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'Error al actualizar la contraseña.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen pt-24 pb-12 px-4 bg-mabelita-light flex items-center justify-center">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-100 animate-in fade-in zoom-in duration-300">
                <div className="text-center mb-8">
                    <h1 className="font-serif font-bold text-3xl text-mabelita-dark mb-2">
                        Restablecer Contraseña
                    </h1>
                    <p className="text-gray-500">
                        Ingresa tu nueva contraseña para acceder a tu cuenta.
                    </p>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-4">
                    {message && (
                        <div className={`p-3 rounded-lg text-sm text-center font-medium ${message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                            {message.text}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nueva Contraseña <span className="text-red-500">*</span></label>
                        <input
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
                            placeholder="••••••••"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Contraseña <span className="text-red-500">*</span></label>
                        <input
                            type="password"
                            required
                            minLength={6}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-mabelita-primary focus:border-transparent outline-none transition-all"
                            placeholder="••••••••"
                        />
                    </div>

                    <Button
                        type="submit"
                        disabled={loading || (message?.type === 'error' && message.text.includes('expirado'))}
                        className="w-full mt-6 shadow-md hover:shadow-lg"
                    >
                        {loading ? 'Procesando...' : 'Actualizar Contraseña'}
                    </Button>
                </form>
            </div>
        </div>
    );
};
